import PopUpV2, { IPopUpV2Ref } from "@/modules/ui-elements/PopUpV2";
import { css } from "@emotion/react";
import React, { useImperativeHandle, useMemo, useState } from "react";
import SizesList, { SOCIAL_MEDIA_SIZES } from "../LeftToolBar/Resize/SizesList";
import DefaultSizes from "../LeftToolBar/Resize/DefaultSizes";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createEmptyEditorJob, getJobDetails, getMarketPlaces } from "../services/Jobs";
import useOtherCategory from "@/hooks/useOtherCategory";
import { useAtomValue, useSetAtom } from "jotai";
import { activeProjectAtom, createNewPopUpAtom, leftNavigationActiveIndexAtom, shouldResizeAtom, shouldSetFormatAtom, standardInfographic } from "../Atoms";
import useToast from "@/hooks/useToast";
import useEditorService from "@/hooks/useEditorService";
import { CANVAS_VIEWPORT_SIZE } from "../constants";
import { useSelector } from "react-redux";
import { kebabCase, snakeCase } from "lodash";

export interface IResizePopUpOptions {
    height: number;
    width: number;
    left: number;
    top: number;
}

export interface SizeItem {
    icon: string;
    name: string;
    height: number;
    width: number;
}

export interface IResizePopUpRef {
    open: (props: IResizePopUpOptions) => void;
    close: () => void;
}

export interface IResizePopUpProps {
    children: React.ReactNode;
    header?: React.ReactNode;
    resizeAction:"create-project" | "create-page";
    onBackdropClick?: () => void;
}

export default React.forwardRef<IResizePopUpRef, IResizePopUpProps>(function ResizePopUp(props,ref) {
   
    const popUpRef = React.useRef<IPopUpV2Ref>(null);
    const {data, isLoading:marketPlaceLoading, isError} = useQuery(["marketplaces"],getMarketPlaces);
    const {otherCategory, otherSubCategory, isLoading:isCategoriesLoading} = useOtherCategory();
    const setActiveProject = useSetAtom<any>(activeProjectAtom);
    const toaster = useToast();
    const [creatingProject,setCreatingProject] = useState(false);
    const setShouldResize= useSetAtom<any>(shouldResizeAtom);
    const queryClient = useQueryClient();
    const {addNewPage, destroy} = useEditorService();
    const setLeftNavigationActiveIndex = useSetAtom(leftNavigationActiveIndexAtom);
    const {entireStrapiData} = useSelector((state:any)=> state.strapi); 
    const strapiData = entireStrapiData?.StudioMyProjectText?.StudioMyProjectText;
    const setShouldSetFormat = useSetAtom(shouldSetFormatAtom)
    const isStandardInfographic = useAtomValue(standardInfographic);
    const setCreateNewPopUp = useSetAtom(createNewPopUpAtom);
    
    const marketPlaceSizes:SizeItem[] = useMemo(()=>{
        return data?.map(item=>({
            icon: item.logo,
            name: item.marketplace_name,
            isGroupLogo:item?.isGroupLogo,
            height:Number(item.dimensionText?.split("x")?.[0]?.replace(" ","")) || 2000,
            width:Number(item.dimensionText?.split("x")?.[1]?.replace(" ","")) || 2000,
        }))
    },[data]);

    const close = () => {
      popUpRef.current?.close();
    }
    
    const open = (props: IResizePopUpOptions) => {
        setCreateNewPopUp(true);
        popUpRef.current?.open({...props, backdrop:true, closeOnBackdropClick:true, backdropOpacity:0.1});
    }


    useImperativeHandle(ref, () => ({open,close}));

    const onResizeGenerator = (item:SizeItem) => async () => {
        if(props.resizeAction === "create-project") {
            if(creatingProject) return;
            if(!otherCategory || !otherSubCategory) return;
            setShouldResize({height:item.height,width:item.width});
            setShouldSetFormat(kebabCase(item.name));
            setCreatingProject(true);
            destroy();
            const key = toaster.info("Creating project...",30000);
            try {
                const jobData = await createEmptyEditorJob(otherCategory._id, otherSubCategory._id, isStandardInfographic);
                const _id = jobData?.job_id;
                const job = await getJobDetails(_id);
                setTimeout(()=>queryClient.invalidateQueries(["projectList"]),500);
                setActiveProject(job);
            }
            catch (error) {
                toaster.error( error.message || typeof error == "string"?error:false ||  "Error creating project!");
            }
            toaster.close(key);
            setCreateNewPopUp(false);
            close();
            setCreatingProject(false);
        }
        if(props.resizeAction === "create-page") {
            addNewPage(CANVAS_VIEWPORT_SIZE, {width:item.width,height:item.height});
            setCreateNewPopUp(false);
            close();
        }
        setLeftNavigationActiveIndex(1);
    }

    return <React.Fragment>
        <PopUpV2 ref={popUpRef} onBackdropClick={props.onBackdropClick}>
            <div style={{background:'white', flex:1, overflowY:"scroll"}}>
                <DefaultSizes disabled={isCategoriesLoading || creatingProject} onResizeGenerator={onResizeGenerator} />
                <SizesList disabled={isCategoriesLoading || creatingProject} list={SOCIAL_MEDIA_SIZES} title={strapiData?.SocialMediaSizesText || "Social Media Sizes"} onResizeGenerator={onResizeGenerator} />
                <SizesList disabled={isCategoriesLoading || creatingProject} list={marketPlaceSizes} title={strapiData?.MarketplaceSizeText || "Marketplace Sizes"} onResizeGenerator={onResizeGenerator} />
            </div>
        </PopUpV2>
    </React.Fragment>
});
